import { useState } from 'preact/hooks'
import { RotateCcw, ChevronDown } from 'lucide-preact'
import { DELIVERY_STATUS_PRESENTATION, type OutboundWebhookDeliveryViewDto } from '@/types/outboundWebhooks'

interface DeliveryRowProps {
    delivery: OutboundWebhookDeliveryViewDto
    maxAttempts: number
    /** Resolves the row's workspace id to its name; falls back to the id when it cannot. */
    workspaceName: (id: string) => string
    onResend: (deliveryId: string) => Promise<void>
}

// The API emits some UTC values with no designator, which parse as local time. Appending Z fixes
// those, but appending it to a value that already carries an offset yields an Invalid Date, so test
// for either form rather than for Z alone.
const HAS_TIME_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i

const relativeTime = (iso: string): string => {
    const then = new Date(HAS_TIME_ZONE.test(iso) ? iso : `${iso}Z`).getTime()
    const seconds = Math.max(0, Math.round((Date.now() - then) / 1000))
    if (seconds < 60) return `${seconds}s ago`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`
    return `${Math.round(seconds / 86400)}d ago`
}

/**
 * One delivery, expanding in place to show the failure detail.
 *
 * Inline rather than a modal: PRODUCT.md treats a modal as a confession that the surface could not
 * handle the job, and someone scanning a log for the broken one should be able to open two rows and
 * compare them.
 */
export function DeliveryRow({ delivery, maxAttempts, workspaceName, onResend }: DeliveryRowProps) {
    const [expanded, setExpanded] = useState(false)
    const [resending, setResending] = useState(false)
    const presentation = DELIVERY_STATUS_PRESENTATION[delivery.Status]

    // Pending means the queue may still deliver it, so resending would fire the customer's endpoint
    // twice. Already-superseded rows have a replacement that is the live one.
    const resendable = delivery.Status !== 'Pending' && !delivery.ResentToId
    const resendBlockedReason = delivery.Status === 'Pending'
        ? 'Still being attempted. Resending now would send it twice.'
        : delivery.ResentToId
            ? 'Already resent. The replacement below is the live attempt.'
            : null

    const handleResend = async () => {
        setResending(true)
        try { await onResend(delivery.Id) } finally { setResending(false) }
    }

    const statusLabel = delivery.ResponseStatusCode != null
        ? String(delivery.ResponseStatusCode)
        : presentation.label

    return (
        <>
            <div
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => setExpanded(v => !v)}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v) }
                }}
                className="grid grid-cols-[92px_1fr_auto_28px] sm:grid-cols-[92px_1fr_120px_88px_28px] items-center gap-3 px-3 py-2 rounded-lg text-sm cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150"
            >
                <span className={`inline-flex items-center gap-1.5 justify-self-start px-2 py-0.5 rounded-full text-[11.5px] font-semibold ${presentation.className}`}>
                    <span aria-hidden="true">{presentation.glyph}</span>
                    <span className="sr-only">{presentation.label}: </span>
                    {statusLabel}
                </span>
                <span className="font-medium text-slate-900 dark:text-slate-100 truncate">
                    {delivery.Event === 'webhook.test' ? 'Test delivery' : delivery.LeadId}
                </span>
                <span className="hidden sm:block text-xs text-slate-500 dark:text-slate-400 truncate">
                    {/* The rail and the detail header were taught to resolve this; the log row was
                        missed, so it was the last place still printing a raw id to a customer. */}
                    {workspaceName(delivery.WorkspaceId)}
                </span>
                <span className="text-xs text-slate-400 dark:text-slate-500 tabular-nums text-right">
                    {relativeTime(delivery.CreatedUtc)}
                </span>
                <ChevronDown
                    className={`w-3.5 h-3.5 text-slate-400 justify-self-center transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                />
            </div>

            {expanded && (
                <div className="mx-3 mb-2 px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 flex flex-col gap-2.5">
                    <DetailLine label="Attempts">
                        {/* Counts contacts actually made, so a delivered row reads 1 of 5, not 0 of 5. */}
                        {delivery.Attempts} of {maxAttempts}
                        {delivery.LastAttemptUtc && ` · last at ${new Date(delivery.LastAttemptUtc).toLocaleTimeString()}`}
                    </DetailLine>

                    {delivery.LastError && <DetailLine label="Response">{delivery.LastError}</DetailLine>}
                    {!delivery.LastError && delivery.ResponseStatusCode != null && (
                        <DetailLine label="Response">HTTP {delivery.ResponseStatusCode}</DetailLine>
                    )}

                    <DetailLine label="Endpoint">{delivery.RedactedUrl}</DetailLine>
                    <DetailLine label="Delivery id">{delivery.Id}</DetailLine>
                    {delivery.ResentFromId && <DetailLine label="Resent from">{delivery.ResentFromId}</DetailLine>}
                    {delivery.ResentToId && <DetailLine label="Resent as">{delivery.ResentToId}</DetailLine>}

                    <div className="flex items-center gap-2 pt-0.5">
                        <button
                            type="button"
                            disabled={!resendable || resending}
                            onClick={handleResend}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150"
                        >
                            <RotateCcw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} aria-hidden="true" />
                            {resending ? 'Resending' : 'Resend'}
                        </button>
                        {resendBlockedReason && (
                            <span className="text-xs text-slate-500 dark:text-slate-400">{resendBlockedReason}</span>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

function DetailLine({ label, children }: { label: string; children: preact.ComponentChildren }) {
    return (
        <div className="flex gap-2.5 items-baseline">
            <span className="min-w-[92px] text-[10.5px] font-semibold tracking-wider uppercase text-slate-400 dark:text-slate-500">
                {label}
            </span>
            <span className="font-mono text-xs text-slate-700 dark:text-slate-300 break-words">{children}</span>
        </div>
    )
}
